import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../store";
import { consultOrderActions } from "../store/consultOrder-slice";
import { selectMarketplaceDetailed, selectUserRoles } from "../store/auth-slice";
import { getTokenUserId } from "../utils/tokenUtils";
import { useConsultOrderContext } from "../contexts/ConsultOrderContext";
import {
  getConsultPrefill,
  createConsultOrder,
} from "../services/consultOrderService";
import {
  buildConsultOrderPayload,
  validateConsultOrderDraft,
  ConsultOrderValidationErrors,
} from "../utils/consultOrderPayload";
import { mapConsultSubmitError } from "../utils/consultOrderErrors";
import { ConsultOrderPrefillResponse } from "../types/consultOrder.types";
import { CnCOrderResponse } from "../types/api";
import {
  CONSULT_ORDER_GRACE_MS,
  KEEPALIVE_404_THRESHOLD,
  KEEPALIVE_POLL_INTERVAL_MS,
  MOUNT_PROBE_INTERVAL_MS,
  MOUNT_PROBE_MAX_MS,
  TRIGGER_POLL_INTERVAL_MS,
  TRIGGER_POLL_MAX_MS,
} from "../utils/consultOrder.constants";

// Lifecycle state machine for the ITAM-2583 consult order. Owns ALL lifecycle
// state (phase, per-consult dedupe, ordered set, grace timer, generation
// counters, AbortControllers) and drives three bounded, cancellable polls:
//   1. Trigger poll  (after Join)  -> open the confirmation once on first 200.
//   2. Mount probe   (refresh)     -> show the chip on 200, NEVER auto-open.
//   3. Keep-alive    (modal open)  -> detect 200->404 (debounced) -> grace/clear.

export type ConsultOrderPhase =
  | "IDLE"
  | "ARMING"
  | "PROMPT"
  | "CHIP"
  | "FORM"
  | "SUBMITTING"
  | "SUCCESS";

export interface UseConsultOrderLifecycle {
  phase: ConsultOrderPhase;
  /** Latest fetched prefill (drives the prompt + is hydrated into the draft on confirm). */
  prefill: ConsultOrderPrefillResponse | null;
  chipVisible: boolean;
  graceActive: boolean;
  consultEnded: boolean;
  createdOrder: CnCOrderResponse | null;
  submitError: string | null;
  validationErrors: ConsultOrderValidationErrors;
  /** Increments on each failed submit attempt so the form can focus the first invalid field. */
  submitAttemptId: number;
  lineErrors: Record<number, string>;
  openPrompt: () => void; // CHIP -> PROMPT (chip reopens the confirmation step)
  dismissPrompt: () => void; // PROMPT -> CHIP (decline; no re-prompt)
  confirmPrompt: () => void; // PROMPT -> FORM (hydrate the draft)
  closeForm: () => void; // FORM -> CHIP (PRESERVE the draft)
  closeSuccess: () => void; // SUCCESS -> IDLE (clear the draft)
  submit: () => Promise<void>;
  dismissSubmitError: () => void;
}

export const useConsultOrderLifecycle = (): UseConsultOrderLifecycle => {
  const dispatch = useDispatch();
  const ctx = useConsultOrderContext();
  const { hydrate, clearDraft } = ctx;

  const armedConsult = useSelector(
    (s: RootState) => s.consultOrder.armedConsult
  );
  const token = useSelector((s: RootState) => s.auth.token);
  const marketplaceInfo = useSelector(
    (s: RootState) => s.auth.marketplaceInfo
  );
  const marketplaceInfoDetailed = useSelector(selectMarketplaceDetailed);
  const userRoles = useSelector(selectUserRoles);

  const consultantId = token ? getTokenUserId(token) : null;
  const eligible =
    userRoles.includes("PRODAJNO_MESTO_USER") &&
    marketplaceInfo?.takesOrders === 1 &&
    consultantId != null;

  const marketplaceKey = marketplaceInfo
    ? `${marketplaceInfo.marketplaceId}:${marketplaceInfo.branchMarketplaceId}`
    : null;

  const [phase, setPhase] = useState<ConsultOrderPhase>("IDLE");
  const [prefill, setPrefill] = useState<ConsultOrderPrefillResponse | null>(
    null
  );
  const [orderedConsultIds, setOrderedConsultIds] = useState<number[]>([]);
  const [createdOrder, setCreatedOrder] = useState<CnCOrderResponse | null>(
    null
  );
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] =
    useState<ConsultOrderValidationErrors>({});
  const [submitAttemptId, setSubmitAttemptId] = useState(0);
  const [lineErrors, setLineErrors] = useState<Record<number, string>>({});
  const [graceActive, setGraceActive] = useState(false);
  const [consultEnded, setConsultEnded] = useState(false);

  // Refs read inside timers to avoid stale closures / extra effect deps.
  const phaseRef = useRef(phase);
  const orderedRef = useRef<Set<number>>(new Set());
  const genRef = useRef(0);
  const promptedRef = useRef<number | null>(null);
  const keepAlive404Ref = useRef(0);
  const graceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const submitAbortRef = useRef<AbortController | null>(null);
  const draftRef = useRef(ctx.draft);
  const ctxPrefillRef = useRef(ctx.prefill);

  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);
  useEffect(() => {
    orderedRef.current = new Set(orderedConsultIds);
  }, [orderedConsultIds]);
  useEffect(() => {
    draftRef.current = ctx.draft;
    ctxPrefillRef.current = ctx.prefill;
  });

  const clearGraceTimer = useCallback(() => {
    if (graceTimerRef.current) {
      clearTimeout(graceTimerRef.current);
      graceTimerRef.current = null;
    }
  }, []);

  const resetToIdle = useCallback(() => {
    clearGraceTimer();
    genRef.current += 1; // invalidate any in-flight poll
    promptedRef.current = null;
    keepAlive404Ref.current = 0;
    if (submitAbortRef.current) {
      submitAbortRef.current.abort();
      submitAbortRef.current = null;
    }
    setConsultEnded(false);
    setGraceActive(false);
    setPrefill(null);
    setSubmitError(null);
    setValidationErrors({});
    setLineErrors({});
    setCreatedOrder(null);
    clearDraft();
    setPhase("IDLE");
    dispatch(consultOrderActions.clearArmed());
  }, [clearGraceTimer, clearDraft, dispatch]);

  const onConsultInactive = useCallback(() => {
    const p = phaseRef.current;
    // A draft is "started" once the form has been entered (context prefill is
    // hydrated). It stays started even after the form is closed back to the
    // chip, so a started draft must never be silently destroyed on consult end.
    const hasStartedDraft = ctxPrefillRef.current != null;
    if (p === "FORM" || p === "SUBMITTING" || (p === "CHIP" && hasStartedDraft)) {
      // Grace window: keep the draft submittable, block a NEW draft (the arm
      // effect only fires from IDLE/CHIP), show a banner. Phase is left as-is
      // so a chip-closed draft can still be reopened and sent during grace.
      clearGraceTimer();
      setConsultEnded(true);
      setGraceActive(true);
      graceTimerRef.current = setTimeout(() => {
        resetToIdle();
      }, CONSULT_ORDER_GRACE_MS);
    } else {
      // No started draft (bare PROMPT/CHIP from a probe) -> just clear.
      resetToIdle();
    }
  }, [clearGraceTimer, resetToIdle]);

  // --- Arm reaction: Join signal -> ARMING (only from IDLE/CHIP) ------------
  useEffect(() => {
    const id = armedConsult?.consultId;
    if (id == null) {
      return;
    }
    if (orderedRef.current.has(id)) {
      return; // FE one-order-per-consult guard
    }
    if (phaseRef.current === "IDLE" || phaseRef.current === "CHIP") {
      promptedRef.current = null;
      setPrefill(null);
      setPhase("ARMING");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [armedConsult?.consultId]);

  // --- Trigger poll (phase ARMING) -----------------------------------------
  useEffect(() => {
    if (phase !== "ARMING" || consultantId == null) {
      return;
    }
    let cancelled = false;
    let settled = false;
    let busy = false; // in-flight guard: never overlap poll requests
    let inFlight: AbortController | null = null;
    const gen = ++genRef.current;
    let elapsed = 0;

    const tick = async () => {
      if (cancelled || settled || busy) {
        return;
      }
      busy = true;
      const controller = new AbortController();
      inFlight = controller;
      try {
        const result = await getConsultPrefill(consultantId, controller.signal);
        if (cancelled || settled || gen !== genRef.current) {
          return;
        }
        if (result) {
          settled = true;
          if (orderedRef.current.has(result.consultId)) {
            resetToIdle();
            return;
          }
          setPrefill(result);
          if (promptedRef.current !== result.consultId) {
            promptedRef.current = result.consultId; // auto-open ONCE
            setPhase("PROMPT");
          } else {
            setPhase("CHIP");
          }
        }
        // 404 -> keep polling until the deadline.
      } catch {
        // transient error -> keep polling until the deadline.
      } finally {
        busy = false;
        if (inFlight === controller) {
          inFlight = null;
        }
      }
    };

    tick();
    const interval = setInterval(() => {
      elapsed += TRIGGER_POLL_INTERVAL_MS;
      if (elapsed >= TRIGGER_POLL_MAX_MS) {
        cancelled = true;
        clearInterval(interval);
        if (inFlight) {
          inFlight.abort();
        }
        if (phaseRef.current === "ARMING") {
          resetToIdle(); // exhausted with no 200
        }
        return;
      }
      tick();
    }, TRIGGER_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(interval);
      if (inFlight) {
        inFlight.abort();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, consultantId]);

  // --- Mount probe (phase IDLE, eligible, not armed) -----------------------
  useEffect(() => {
    if (phase !== "IDLE" || !eligible || consultantId == null) {
      return;
    }
    if (armedConsult?.consultId != null) {
      return; // the arm path will handle it
    }
    let cancelled = false;
    let settled = false;
    let busy = false; // in-flight guard: never overlap probe requests
    let inFlight: AbortController | null = null;
    const gen = ++genRef.current;
    let elapsed = 0;

    const probe = async () => {
      if (cancelled || settled || busy) {
        return;
      }
      busy = true;
      const controller = new AbortController();
      inFlight = controller;
      try {
        const result = await getConsultPrefill(consultantId, controller.signal);
        if (cancelled || settled || gen !== genRef.current) {
          return;
        }
        if (result) {
          settled = true;
          if (orderedRef.current.has(result.consultId)) {
            return; // already ordered -> no chip
          }
          setPrefill(result);
          setPhase("CHIP"); // NEVER auto-open after refresh
        }
      } catch {
        // ignore; bounded window will end the probe
      } finally {
        busy = false;
        if (inFlight === controller) {
          inFlight = null;
        }
      }
    };

    probe();
    const interval = setInterval(() => {
      elapsed += MOUNT_PROBE_INTERVAL_MS;
      if (elapsed >= MOUNT_PROBE_MAX_MS) {
        cancelled = true;
        clearInterval(interval);
        if (inFlight) {
          inFlight.abort();
        }
        return;
      }
      probe();
    }, MOUNT_PROBE_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(interval);
      if (inFlight) {
        inFlight.abort();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, eligible, consultantId, armedConsult?.consultId]);

  // --- Keep-alive poll (a modal/chip is holding a 200 prefill) -------------
  useEffect(() => {
    const active =
      phase === "PROMPT" ||
      phase === "CHIP" ||
      phase === "FORM" ||
      phase === "SUBMITTING";
    // Once the consult has ENDED (grace running), stop keep-alive entirely: the
    // grace timer alone owns the reset. Otherwise closing the form to the chip
    // during grace would restart keep-alive, hit the 404 threshold again, and
    // reset to IDLE — collapsing the grace window and wiping the draft early.
    if (!active || consultEnded || consultantId == null) {
      return;
    }
    let cancelled = false;
    let busy = false; // in-flight guard: never overlap poll requests
    let inFlight: AbortController | null = null;
    const gen = ++genRef.current;
    keepAlive404Ref.current = 0;

    const tick = async () => {
      if (cancelled || busy) {
        return;
      }
      busy = true;
      const controller = new AbortController();
      inFlight = controller;
      try {
        const result = await getConsultPrefill(consultantId, controller.signal);
        if (cancelled || gen !== genRef.current) {
          return;
        }
        if (result) {
          keepAlive404Ref.current = 0; // still active
        } else {
          keepAlive404Ref.current += 1;
          if (keepAlive404Ref.current >= KEEPALIVE_404_THRESHOLD) {
            cancelled = true;
            clearInterval(interval);
            onConsultInactive();
          }
        }
      } catch {
        // transient error -> neither confirm nor count as a 404
      } finally {
        busy = false;
        if (inFlight === controller) {
          inFlight = null;
        }
      }
    };

    const interval = setInterval(tick, KEEPALIVE_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(interval);
      if (inFlight) {
        inFlight.abort();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, consultEnded, consultantId, onConsultInactive]);

  // --- Marketplace / consultant change: full reset -------------------------
  const prevKeyRef = useRef(marketplaceKey);
  const prevConsultantRef = useRef(consultantId);
  useEffect(() => {
    if (
      prevKeyRef.current !== marketplaceKey ||
      prevConsultantRef.current !== consultantId
    ) {
      prevKeyRef.current = marketplaceKey;
      prevConsultantRef.current = consultantId;
      setOrderedConsultIds([]);
      resetToIdle();
    }
  }, [marketplaceKey, consultantId, resetToIdle]);

  // --- Unmount cleanup -----------------------------------------------------
  useEffect(
    () => () => {
      clearGraceTimer();
      genRef.current += 1;
      if (submitAbortRef.current) {
        submitAbortRef.current.abort();
      }
    },
    [clearGraceTimer]
  );

  // --- Actions -------------------------------------------------------------
  const openPrompt = useCallback(() => {
    setPhase((p) => (p === "CHIP" ? "PROMPT" : p));
  }, []);

  const dismissPrompt = useCallback(() => {
    setPhase((p) => (p === "PROMPT" ? "CHIP" : p));
  }, []);

  const confirmPrompt = useCallback(() => {
    if (!prefill) {
      return;
    }
    // Hydrate the draft only on the FIRST entry to the form for THIS consult.
    // Re-entering via the chip (CHIP -> PROMPT -> confirm) must PRESERVE the
    // pharmacist's edits; re-hydrating would reset the draft to the prefill and
    // silently discard added items / delivery choices.
    if (ctxPrefillRef.current?.consultId !== prefill.consultId) {
      hydrate(prefill);
    }
    setValidationErrors({});
    setSubmitError(null);
    setLineErrors({});
    setPhase("FORM");
  }, [prefill, hydrate]);

  const closeForm = useCallback(() => {
    // Preserve the draft; the chip reopens via the confirmation step.
    setPhase((p) => (p === "FORM" ? "CHIP" : p));
  }, []);

  const closeSuccess = useCallback(() => {
    resetToIdle();
  }, [resetToIdle]);

  const dismissSubmitError = useCallback(() => {
    setSubmitError(null);
    setLineErrors({});
  }, []);

  const submit = useCallback(async () => {
    if (phaseRef.current === "SUBMITTING") {
      return; // in-flight guard
    }
    const draft = draftRef.current;
    const activePrefill = ctxPrefillRef.current;
    if (!activePrefill || !marketplaceInfo) {
      return;
    }

    const { valid, errors } = validateConsultOrderDraft(
      draft,
      activePrefill,
      marketplaceInfoDetailed
    );
    if (!valid) {
      setValidationErrors(errors);
      // Bump the attempt nonce so the form focuses/scrolls to the first invalid
      // field (the button itself is never pre-disabled by validity anymore).
      setSubmitAttemptId((n) => n + 1);
      return;
    }
    setValidationErrors({});
    setSubmitError(null);
    setLineErrors({});
    setPhase("SUBMITTING");

    const controller = new AbortController();
    submitAbortRef.current = controller;
    try {
      const payload = buildConsultOrderPayload(draft, activePrefill, {
        marketplaceId: marketplaceInfo.marketplaceId,
        branchMarketplaceId: marketplaceInfo.branchMarketplaceId,
      });
      const order = await createConsultOrder(payload, controller.signal);
      if (controller.signal.aborted) {
        return;
      }
      // The consult-end grace timer must not outlive a completed submit:
      // left armed, it would fire resetToIdle mid-SUCCESS and wipe the
      // confirmation (and created order number) out from under the pharmacist.
      clearGraceTimer();
      setCreatedOrder(order);
      setOrderedConsultIds((prev) =>
        prev.includes(activePrefill.consultId)
          ? prev
          : [...prev, activePrefill.consultId]
      );
      setPhase("SUCCESS");
    } catch (error) {
      if (controller.signal.aborted) {
        return;
      }
      const mapped = mapConsultSubmitError(error);
      if (mapped.is409) {
        // Already created -> treat as success (hide chip, mark ordered).
        clearGraceTimer(); // same as the success path: SUCCESS must not be wiped
        setOrderedConsultIds((prev) =>
          prev.includes(activePrefill.consultId)
            ? prev
            : [...prev, activePrefill.consultId]
        );
        setCreatedOrder(null);
        setSubmitError(mapped.message);
        setPhase("SUCCESS");
        return;
      }
      setSubmitError(mapped.message);
      if (mapped.lineErrors) {
        setLineErrors(mapped.lineErrors);
      }
      setPhase("FORM"); // keep the draft
    } finally {
      submitAbortRef.current = null;
    }
  }, [marketplaceInfo, marketplaceInfoDetailed, clearGraceTimer]);

  return useMemo(
    () => ({
      phase,
      prefill,
      chipVisible: phase === "CHIP",
      graceActive,
      consultEnded,
      createdOrder,
      submitError,
      validationErrors,
      submitAttemptId,
      lineErrors,
      openPrompt,
      dismissPrompt,
      confirmPrompt,
      closeForm,
      closeSuccess,
      submit,
      dismissSubmitError,
    }),
    [
      phase,
      prefill,
      graceActive,
      consultEnded,
      createdOrder,
      submitError,
      validationErrors,
      submitAttemptId,
      lineErrors,
      openPrompt,
      dismissPrompt,
      confirmPrompt,
      closeForm,
      closeSuccess,
      submit,
      dismissSubmitError,
    ]
  );
};
